/**
 * Componente Checkbox
 *
 * Checkbox accesible con animación de check.
 * Optimizado para feedback táctil y visual.
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '@/core/utils';
import type { Layer } from '@/core/types';
import { getLayerColors } from '@/core/constants';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  layer?: Layer;
  label?: string;
  description?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ layer = 'maintenance', label, description, className, checked, ...props }, ref) => {
    const colors = getLayerColors(layer);

    return (
      <label className={cn('flex items-start gap-3 cursor-pointer group', className)}>
        <div className="relative flex-shrink-0 mt-0.5">
          <input
            ref={ref}
            type="checkbox"
            checked={checked}
            className="sr-only peer"
            {...props}
          />
          <motion.div
            className={cn(
              'w-5 h-5 rounded-md border-2 flex items-center justify-center',
              'transition-colors duration-200',
              checked
                ? `${colors.bg} border-transparent`
                : 'border-slate-600 group-hover:border-slate-500'
            )}
            whileTap={{ scale: 0.9 }}
          >
            {checked && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
              >
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </motion.div>
            )}
          </motion.div>
        </div>

        {(label ?? description) && (
          <div className="flex-1 min-w-0">
            {label && (
              <span
                className={cn(
                  'block text-sm font-medium transition-colors',
                  checked ? 'text-slate-500 line-through' : 'text-slate-200'
                )}
              >
                {label}
              </span>
            )}
            {description && (
              <span className="block text-xs text-slate-500 mt-0.5">
                {description}
              </span>
            )}
          </div>
        )}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
